/**
 * @fileoverview PersistGate component for handling Redux store persistence and user session validation.
 */

import {ReactNode} from "react";
import {connect} from "react-redux";
import {PersistGate as ReduxPersistGate} from "redux-persist/integration/react";
import {UserDto} from "../utils/types/user";

import {Dispatch, persistor, select} from "./store";
import {history} from "../utils/history";
import {Spinner} from "../atoms/spinner";
import {useRefreshUserProfile} from "../hooks/useRefreshUserProfileQuery";

/**
 * Props for dispatch actions in the PersistGate component.
 */
type DispatchProps = Readonly<{
  setUser: ({
    user,
    isUserLogin,
  }: {
    user: UserDto;
    isUserLogin: boolean;
  }) => Promise<void>;
}>;

/**
 * Renderer component that handles user session validation during store rehydration.
 * We use the gate to refresh user profile, to detect if the user session is still active.
 * This is done in a bit hacky way - initially the PersistGate connects to empty store,
 * but after it rehydrates the store, we get the user, and if there is one, we do an api call
 * to check if the session is still valid.
 * If there is no user, or we enter non-admin part of the app, we skip the api call.
 * We leverage the `onBeforeLift` which causes the loading spinner to be rendered
 * while the promise returned to the callback is pending.
 *
 * @param {Object} props Component props.
 * @param {UserDto} [props.user] The current user from the store.
 * @param {Function} props.setUser Function to update user data.
 * @param {ReactNode} props.children Child components to render.
 * @returns {JSX.Element} The PersistGate component with user validation.
 */
const Renderer = ({
  user,
  setUser,
  children,
}: {user?: UserDto; children: ReactNode} & DispatchProps) => {
  const isAdminApp = history.location.pathname.startsWith("/admin");

  const {refetch} = useRefreshUserProfile({
    userId: user?.id,
    setUser: user => setUser({user, isUserLogin: false}),
  });

  return (
    <ReduxPersistGate
      persistor={persistor}
      loading={<Spinner />}
      onBeforeLift={() => (isAdminApp && user?.id ? refetch() : undefined)}
    >
      {children}
    </ReduxPersistGate>
  );
};

/**
 * Maps Redux state to component props.
 */
const mapState = (state: any) => ({
  user: select.adminSession.user(state),
});

/**
 * Maps Redux dispatch actions to component props.
 */
const mapDispatch = (dispatch: Dispatch): DispatchProps => ({
  setUser: dispatch.adminSession.setUser,
});

/**
 * Connected PersistGate component that handles store persistence and user session validation.
 * Wraps the application to ensure the store is rehydrated before rendering children.
 */
export const PersistGate = connect(mapState, mapDispatch)(Renderer);
